import React from 'react';
import { View, Text, Pressable } from 'react-native';
import SearchBox from '@/components/General/inputs/SearchBox';
import Unicon from '@/components/General/Unicon';
import DropdownMenu from '@/components/General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';

export const SORT_OPTIONS = [
  { key: 'name-asc', label: 'Name (A–Z)' },
  { key: 'name-desc', label: 'Name (Z–A)' },
  { key: 'date-desc', label: 'Newest first' },
  { key: 'date-asc', label: 'Oldest first' },
];

const Toolbar = ({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewToggle,
  sortKey,
  onSortChange,
  selectedCount,
  onBulkMove,
  onBulkDelete,
  onClearSelection,
}) => {
  if (selectedCount > 0) {
    return (
      <View style={styles.row}>
        <Text style={styles.selectedText}>
          {selectedCount} selected
        </Text>
        <View style={styles.actions}>
          <DropdownMenu
            testID="bulk-actions-menu"
            triggerComponent={
              <View style={styles.iconBtn}>
                <Unicon name="ellipsis-horizontal" size={22} colorVariant="text" />
              </View>
            }
            options={[
              { name: 'Move', icon: { web: 'folder-outline', ios: 'folder', android: 'drive_file_move' }, onSelect: () => onBulkMove?.() },
              { name: 'Delete', icon: { web: 'trash', ios: 'trash', android: 'delete' }, destructive: true, onSelect: () => onBulkDelete?.() },
            ]}
          />
          <Pressable onPress={onClearSelection} style={styles.iconBtn} hitSlop={8}>
            <Unicon name="close" size={22} colorVariant="textSecondary" />
          </Pressable>
        </View>
      </View>
    );
  }

  const currentSort = SORT_OPTIONS.find(o => o.key === sortKey) || SORT_OPTIONS[0];

  return (
    <View style={styles.row}>
      <View style={styles.searchWrap}>
        <SearchBox
          testID="files-search-box"
          searchText={searchQuery}
          onChangeText={onSearchChange}
          placeholder="Search files and folders..."
          textChangeDelay={300}
        />
      </View>

      <View style={styles.controls}>
        <DropdownMenu
          title="Sort by"
          triggerComponent={
            <View style={styles.sortTrigger}>
              <Unicon name="swap-vertical-outline" size={14} colorVariant="primary" />
              <Text style={styles.sortLabel} numberOfLines={1}>
                {currentSort.label}
              </Text>
              <Unicon name="chevron-down" size={14} colorVariant="textSecondary" />
            </View>
          }
          options={SORT_OPTIONS.map(o => ({
            name: o.label,
            selected: o.key === sortKey,
            onSelect: () => onSortChange(o.key),
          }))}
        />

        <View style={styles.viewToggle}>
          <Pressable onPress={() => onViewToggle('list')} style={styles.viewToggleBtn(viewMode === 'list')}>
            <Unicon name="list" size={16} colorVariant={viewMode === 'list' ? 'primary' : 'textSecondary'} />
          </Pressable>
          <Pressable onPress={() => onViewToggle('grid')} style={styles.viewToggleBtn(viewMode === 'grid')}>
            <Unicon name="grid" size={16} colorVariant={viewMode === 'grid' ? 'primary' : 'textSecondary'} />
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create(({ colors }) => ({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: 10,
  },
  searchWrap: {
    flex: 1,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  sortTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: colors.surface,
    _web: { cursor: 'pointer', _hover: { opacity: 0.85 } },
  },
  sortLabel: {
    fontSize: 13,
    color: colors.text,
  },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 2,
  },
  viewToggleBtn: (isActive) => ({
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: isActive ? colors.background : 'transparent',
    _web: { cursor: 'pointer', _hover: { opacity: 0.85 } },
  }),
  selectedText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
    color: colors.text,
  },
  actions: {
    flexDirection: 'row',
    gap: 4,
  },
  iconBtn: {
    padding: 6,
  },
}));

export default Toolbar;
